// Shared meeting notes over the reliable data channel (Meeting Tools). Like
// polls/timer, the payload rides the E2EE data path — the SFU relays only
// ciphertext, so note content, cursors and presence are never visible to our
// infrastructure. Ephemeral by design; nothing here persists past the meeting.
//
// Collaboration is a CRDT (Yjs) that lives in the ui-web notes store; this
// module is deliberately Yjs-agnostic so call-engine stays dependency-free. It
// only frames the opaque byte payloads the store exchanges, tagged by kind:
//   u  document update   (a Yjs update — the edits themselves)
//   a  awareness update  (cursors / selection / presence)
//   q  sync request      (a Yjs state vector — "send me what I'm missing")

export const NOTES_TOPIC = 'bz.notes';

export type NoteKind = 'u' | 'a' | 'q';

export interface NoteWire {
  kind: NoteKind;
  /** Opaque CRDT bytes (a Yjs update / awareness update / state vector). */
  data: Uint8Array;
}

const KIND_CODE: Record<NoteKind, number> = { u: 1, a: 2, q: 3 };
const CODE_KIND: Record<number, NoteKind | undefined> = { 1: 'u', 2: 'a', 3: 'q' };

/** A single frame that fits comfortably under the data-channel ceiling; the
 * store already emits Yjs updates incrementally, so frames stay small. A
 * defensive upper bound guards against a malformed or hostile packet. */
const MAX_FRAME = 256 * 1024;

export function encodeNote(kind: NoteKind, data: Uint8Array): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(1 + data.length);
  out[0] = KIND_CODE[kind];
  out.set(data, 1);
  return out;
}

export function decodeNote(payload: Uint8Array): NoteWire | null {
  if (payload.length < 1 || payload.length > MAX_FRAME + 1) return null;
  const code = payload[0];
  const kind = code === undefined ? undefined : CODE_KIND[code];
  if (!kind) return null;
  // subarray shares the buffer; the store applies it synchronously on receipt.
  return { kind, data: payload.subarray(1) };
}
